import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBandwidth } from '../hooks/useBandwidth';
import { usePricing } from '../hooks/usePricing';

const generateId = () => Math.random().toString(36).substring(2, 10);

const RotatingResidentialPremiumPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { packages, purchasePackage, loading: packagesLoading } = useBandwidth();
  const { allTiers, calculatePrice, loading: pricingLoading } = usePricing();
  const hasPremiumAccount = !!profile?.stock_subuser_id_premium;
  const [activeTab, setActiveTab] = useState(hasPremiumAccount ? 'configuration' : 'pricing');

  // Purchase modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(10);
  const [purchaseInputValue, setPurchaseInputValue] = useState('10');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [multiSelect, setMultiSelect] = useState(true);
  const [selectedCountries, setSelectedCountries] = useState(['global']);
  const [selectedCities, setSelectedCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionEnabled, setSessionEnabled] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(10);
  const [sessionCount, setSessionCount] = useState(1);
  const [sessionIds] = useState(() => [generateId()]);
  const [, setSessionIdsState] = useState(0);
  const [fastMode, setFastMode] = useState(false);
  const [proxyProtocol, setProxyProtocol] = useState('none');
  const [outputFormat, setOutputFormat] = useState('host:port:username:password');
  const [copied, setCopied] = useState(false);

  const getSessionIds = () => {
    while (sessionIds.length < sessionCount) {
      sessionIds.push(generateId());
    }
    return sessionIds.slice(0, sessionCount);
  };

  const handleSessionCountChange = (count) => {
    setSessionCount(count);
    while (sessionIds.length < count) {
      sessionIds.push(generateId());
    }
    setSessionIdsState(prev => prev + 1);
  };

  const regenerateSessionIds = () => {
    sessionIds.length = 0;
    for (let i = 0; i < sessionCount; i++) {
      sessionIds.push(generateId());
    }
    setSessionIdsState(prev => prev + 1);
  };

  const countries = [
    { code: 'global', name: 'Global (All Countries)', flag: '\u{1F30D}' },
    { code: 'us', name: 'United States', flag: '\u{1F1FA}\u{1F1F8}' },
    { code: 'gb', name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}' },
    { code: 'de', name: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
    { code: 'fr', name: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
    { code: 'nl', name: 'Netherlands', flag: '\u{1F1F3}\u{1F1F1}' },
    { code: 'ca', name: 'Canada', flag: '\u{1F1E8}\u{1F1E6}' },
    { code: 'jp', name: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
    { code: 'au', name: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
    { code: 'br', name: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
    { code: 'in', name: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
    { code: 'it', name: 'Italy', flag: '\u{1F1EE}\u{1F1F9}' },
    { code: 'es', name: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
    { code: 'mx', name: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
    { code: 'pl', name: 'Poland', flag: '\u{1F1F5}\u{1F1F1}' },
    { code: 'ru', name: 'Russia', flag: '\u{1F1F7}\u{1F1FA}' },
    { code: 'kr', name: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
    { code: 'sg', name: 'Singapore', flag: '\u{1F1F8}\u{1F1EC}' },
    { code: 'se', name: 'Sweden', flag: '\u{1F1F8}\u{1F1EA}' },
    { code: 'ch', name: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
    { code: 'tr', name: 'Turkey', flag: '\u{1F1F9}\u{1F1F7}' },
    { code: 'ua', name: 'Ukraine', flag: '\u{1F1FA}\u{1F1E6}' },
    { code: 'ar', name: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
    { code: 'be', name: 'Belgium', flag: '\u{1F1E7}\u{1F1EA}' },
    { code: 'at', name: 'Austria', flag: '\u{1F1E6}\u{1F1F9}' },
    { code: 'pt', name: 'Portugal', flag: '\u{1F1F5}\u{1F1F9}' },
    { code: 'dk', name: 'Denmark', flag: '\u{1F1E9}\u{1F1F0}' },
    { code: 'no', name: 'Norway', flag: '\u{1F1F3}\u{1F1F4}' },
    { code: 'fi', name: 'Finland', flag: '\u{1F1EB}\u{1F1EE}' },
    { code: 'ie', name: 'Ireland', flag: '\u{1F1EE}\u{1F1EA}' },
    { code: 'cz', name: 'Czech Republic', flag: '\u{1F1E8}\u{1F1FF}' },
    { code: 'ro', name: 'Romania', flag: '\u{1F1F7}\u{1F1F4}' },
    { code: 'hu', name: 'Hungary', flag: '\u{1F1ED}\u{1F1FA}' },
    { code: 'bg', name: 'Bulgaria', flag: '\u{1F1E7}\u{1F1EC}' },
    { code: 'hr', name: 'Croatia', flag: '\u{1F1ED}\u{1F1F7}' },
    { code: 'sk', name: 'Slovakia', flag: '\u{1F1F8}\u{1F1F0}' },
    { code: 'si', name: 'Slovenia', flag: '\u{1F1F8}\u{1F1EE}' },
    { code: 'lt', name: 'Lithuania', flag: '\u{1F1F1}\u{1F1F9}' },
    { code: 'lv', name: 'Latvia', flag: '\u{1F1F1}\u{1F1FB}' },
    { code: 'ee', name: 'Estonia', flag: '\u{1F1EA}\u{1F1EA}' },
    { code: 'gr', name: 'Greece', flag: '\u{1F1EC}\u{1F1F7}' },
    { code: 'rs', name: 'Serbia', flag: '\u{1F1F7}\u{1F1F8}' },
    { code: 'il', name: 'Israel', flag: '\u{1F1EE}\u{1F1F1}' },
    { code: 'ae', name: 'United Arab Emirates', flag: '\u{1F1E6}\u{1F1EA}' },
    { code: 'sa', name: 'Saudi Arabia', flag: '\u{1F1F8}\u{1F1E6}' },
    { code: 'za', name: 'South Africa', flag: '\u{1F1FF}\u{1F1E6}' },
    { code: 'eg', name: 'Egypt', flag: '\u{1F1EA}\u{1F1EC}' },
    { code: 'ng', name: 'Nigeria', flag: '\u{1F1F3}\u{1F1EC}' },
    { code: 'ke', name: 'Kenya', flag: '\u{1F1F0}\u{1F1EA}' },
    { code: 'gh', name: 'Ghana', flag: '\u{1F1EC}\u{1F1ED}' },
    { code: 'ma', name: 'Morocco', flag: '\u{1F1F2}\u{1F1E6}' },
    { code: 'tn', name: 'Tunisia', flag: '\u{1F1F9}\u{1F1F3}' },
    { code: 'th', name: 'Thailand', flag: '\u{1F1F9}\u{1F1ED}' },
    { code: 'vn', name: 'Vietnam', flag: '\u{1F1FB}\u{1F1F3}' },
    { code: 'id', name: 'Indonesia', flag: '\u{1F1EE}\u{1F1E9}' },
    { code: 'my', name: 'Malaysia', flag: '\u{1F1F2}\u{1F1FE}' },
    { code: 'ph', name: 'Philippines', flag: '\u{1F1F5}\u{1F1ED}' },
    { code: 'tw', name: 'Taiwan', flag: '\u{1F1F9}\u{1F1FC}' },
    { code: 'hk', name: 'Hong Kong', flag: '\u{1F1ED}\u{1F1F0}' },
    { code: 'cn', name: 'China', flag: '\u{1F1E8}\u{1F1F3}' },
    { code: 'pk', name: 'Pakistan', flag: '\u{1F1F5}\u{1F1F0}' },
    { code: 'bd', name: 'Bangladesh', flag: '\u{1F1E7}\u{1F1E9}' },
    { code: 'nz', name: 'New Zealand', flag: '\u{1F1F3}\u{1F1FF}' },
    { code: 'cl', name: 'Chile', flag: '\u{1F1E8}\u{1F1F1}' },
    { code: 'co', name: 'Colombia', flag: '\u{1F1E8}\u{1F1F4}' },
    { code: 'pe', name: 'Peru', flag: '\u{1F1F5}\u{1F1EA}' },
    { code: 've', name: 'Venezuela', flag: '\u{1F1FB}\u{1F1EA}' },
    { code: 'ec', name: 'Ecuador', flag: '\u{1F1EA}\u{1F1E8}' },
    { code: 'uy', name: 'Uruguay', flag: '\u{1F1FA}\u{1F1FE}' },
    { code: 'cr', name: 'Costa Rica', flag: '\u{1F1E8}\u{1F1F7}' },
    { code: 'pa', name: 'Panama', flag: '\u{1F1F5}\u{1F1E6}' },
    { code: 'do', name: 'Dominican Republic', flag: '\u{1F1E9}\u{1F1F4}' },
    { code: 'gt', name: 'Guatemala', flag: '\u{1F1EC}\u{1F1F9}' },
    { code: 'by', name: 'Belarus', flag: '\u{1F1E7}\u{1F1FE}' },
    { code: 'md', name: 'Moldova', flag: '\u{1F1F2}\u{1F1E9}' },
    { code: 'ge', name: 'Georgia', flag: '\u{1F1EC}\u{1F1EA}' },
    { code: 'kz', name: 'Kazakhstan', flag: '\u{1F1F0}\u{1F1FF}' },
    { code: 'uz', name: 'Uzbekistan', flag: '\u{1F1FA}\u{1F1FF}' },
    { code: 'az', name: 'Azerbaijan', flag: '\u{1F1E6}\u{1F1FF}' },
    { code: 'qa', name: 'Qatar', flag: '\u{1F1F6}\u{1F1E6}' },
    { code: 'kw', name: 'Kuwait', flag: '\u{1F1F0}\u{1F1FC}' },
    { code: 'bh', name: 'Bahrain', flag: '\u{1F1E7}\u{1F1ED}' },
    { code: 'om', name: 'Oman', flag: '\u{1F1F4}\u{1F1F2}' },
    { code: 'jo', name: 'Jordan', flag: '\u{1F1EF}\u{1F1F4}' },
    { code: 'lb', name: 'Lebanon', flag: '\u{1F1F1}\u{1F1E7}' },
    { code: 'cy', name: 'Cyprus', flag: '\u{1F1E8}\u{1F1FE}' },
    { code: 'mt', name: 'Malta', flag: '\u{1F1F2}\u{1F1F9}' },
    { code: 'is', name: 'Iceland', flag: '\u{1F1EE}\u{1F1F8}' },
    { code: 'lu', name: 'Luxembourg', flag: '\u{1F1F1}\u{1F1FA}' },
    { code: 'al', name: 'Albania', flag: '\u{1F1E6}\u{1F1F1}' },
    { code: 'ba', name: 'Bosnia and Herzegovina', flag: '\u{1F1E7}\u{1F1E6}' },
    { code: 'me', name: 'Montenegro', flag: '\u{1F1F2}\u{1F1EA}' },
    { code: 'mk', name: 'North Macedonia', flag: '\u{1F1F2}\u{1F1F0}' },
    { code: 'lk', name: 'Sri Lanka', flag: '\u{1F1F1}\u{1F1F0}' },
    { code: 'mm', name: 'Myanmar', flag: '\u{1F1F2}\u{1F1F2}' },
    { code: 'kh', name: 'Cambodia', flag: '\u{1F1F0}\u{1F1ED}' },
    { code: 'np', name: 'Nepal', flag: '\u{1F1F3}\u{1F1F5}' },
    { code: 'jm', name: 'Jamaica', flag: '\u{1F1EF}\u{1F1F2}' },
    { code: 'tt', name: 'Trinidad and Tobago', flag: '\u{1F1F9}\u{1F1F9}' },
    { code: 'py', name: 'Paraguay', flag: '\u{1F1F5}\u{1F1FE}' },
    { code: 'bo', name: 'Bolivia', flag: '\u{1F1E7}\u{1F1F4}' },
    { code: 'hn', name: 'Honduras', flag: '\u{1F1ED}\u{1F1F3}' },
    { code: 'sv', name: 'El Salvador', flag: '\u{1F1F8}\u{1F1FB}' },
    { code: 'ni', name: 'Nicaragua', flag: '\u{1F1F3}\u{1F1EE}' },
    { code: 'cu', name: 'Cuba', flag: '\u{1F1E8}\u{1F1FA}' },
    { code: 'tz', name: 'Tanzania', flag: '\u{1F1F9}\u{1F1FF}' },
    { code: 'ug', name: 'Uganda', flag: '\u{1F1FA}\u{1F1EC}' },
    { code: 'cm', name: 'Cameroon', flag: '\u{1F1E8}\u{1F1F2}' },
    { code: 'sn', name: 'Senegal', flag: '\u{1F1F8}\u{1F1F3}' },
    { code: 'ci', name: 'Ivory Coast', flag: '\u{1F1E8}\u{1F1EE}' },
    { code: 'dz', name: 'Algeria', flag: '\u{1F1E9}\u{1F1FF}' },
    { code: 'ly', name: 'Libya', flag: '\u{1F1F1}\u{1F1FE}' },
    { code: 'iq', name: 'Iraq', flag: '\u{1F1EE}\u{1F1F6}' },
    { code: 'ir', name: 'Iran', flag: '\u{1F1EE}\u{1F1F7}' },
    { code: 'mn', name: 'Mongolia', flag: '\u{1F1F2}\u{1F1F3}' },
    { code: 'la', name: 'Laos', flag: '\u{1F1F1}\u{1F1E6}' },
  ];

  const citiesByCountry = {
    us: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'Miami', 'Seattle', 'Denver', 'Boston', 'Atlanta'],
    gb: ['London', 'Birmingham', 'Manchester', 'Glasgow', 'Liverpool', 'Leeds', 'Sheffield', 'Edinburgh', 'Bristol', 'Cardiff'],
    de: ['Berlin', 'Hamburg', 'Munich', 'Cologne', 'Frankfurt', 'Stuttgart', 'Dusseldorf', 'Leipzig', 'Dortmund', 'Essen'],
    fr: ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille'],
    nl: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven', 'Groningen', 'Tilburg', 'Almere'],
    ca: ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton', 'Ottawa', 'Winnipeg', 'Quebec City'],
    jp: ['Tokyo', 'Osaka', 'Yokohama', 'Nagoya', 'Sapporo', 'Kobe', 'Kyoto', 'Fukuoka'],
    au: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Canberra'],
    br: ['Sao Paulo', 'Rio de Janeiro', 'Brasilia', 'Salvador', 'Fortaleza', 'Belo Horizonte'],
    in: ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune'],
    it: ['Rome', 'Milan', 'Naples', 'Turin', 'Palermo', 'Genoa', 'Bologna', 'Florence'],
    es: ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Zaragoza', 'Malaga', 'Bilbao'],
    mx: ['Mexico City', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana', 'Cancun'],
    pl: ['Warsaw', 'Krakow', 'Lodz', 'Wroclaw', 'Poznan', 'Gdansk'],
    ru: ['Moscow', 'Saint Petersburg', 'Novosibirsk', 'Yekaterinburg', 'Kazan'],
    kr: ['Seoul', 'Busan', 'Incheon', 'Daegu', 'Daejeon', 'Gwangju'],
    sg: ['Singapore'],
    se: ['Stockholm', 'Gothenburg', 'Malmo', 'Uppsala'],
    ch: ['Zurich', 'Geneva', 'Basel', 'Bern', 'Lausanne'],
    tr: ['Istanbul', 'Ankara', 'Izmir', 'Bursa', 'Antalya'],
    ua: ['Kyiv', 'Kharkiv', 'Odessa', 'Dnipro', 'Lviv'],
    ar: ['Buenos Aires', 'Cordoba', 'Rosario', 'Mendoza'],
    be: ['Brussels', 'Antwerp', 'Ghent', 'Charleroi', 'Liege'],
    at: ['Vienna', 'Graz', 'Linz', 'Salzburg', 'Innsbruck'],
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedCountry = selectedCountries[0];
  const availableCities = !multiSelect && selectedCountry && selectedCountry !== 'global' ? citiesByCountry[selectedCountry] || [] : [];

  const handleCountryClick = (countryCode) => {
    if (multiSelect) {
      if (countryCode === 'global') {
        setSelectedCountries(['global']);
      } else {
        setSelectedCountries(prev => {
          const withoutGlobal = prev.filter(c => c !== 'global');
          if (prev.includes(countryCode)) {
            const newSelection = withoutGlobal.filter(c => c !== countryCode);
            return newSelection.length === 0 ? ['global'] : newSelection;
          } else {
            return [...withoutGlobal, countryCode];
          }
        });
      }
    } else {
      setSelectedCountries([countryCode]);
      setSelectedCities([]);
    }
  };

  const handleMultiSelectToggle = (enabled) => {
    setMultiSelect(enabled);
    if (enabled) {
      setSelectedCities([]);
    }
  };

  const handleCityClick = (city) => {
    setSelectedCities(prev => prev.includes(city) ? [] : [city]);
  };

  const generateProxyStrings = () => {
    const btUsername = profile?.stock_username_premium || profile?.username || 'user';
    const password = profile?.proxy_password || 'password';

    let countryPart = '';
    if (multiSelect) {
      const nonGlobal = selectedCountries.filter(c => c !== 'global');
      if (nonGlobal.length > 0) {
        countryPart = `_cc-${nonGlobal.map(c => c.toUpperCase()).join(',')}`;
      }
    } else {
      if (selectedCountry && selectedCountry !== 'global') {
        countryPart = `_cc-${selectedCountry.toUpperCase()}`;
      }
    }

    let cityPart = '';
    if (!multiSelect && selectedCities.length > 0) {
      const cityCode = selectedCities[0].toLowerCase().replace(/\s+/g, '');
      cityPart = `_city-${cityCode}`;
    }

    let fastPart = fastMode ? '_settings-fast' : '';

    const host = 'proxy.pathproxy.com';
    const port = '1337';

    const protocolPrefix = proxyProtocol !== 'none' ? `${proxyProtocol}://` : '';

    if (sessionEnabled) {
      const ids = getSessionIds();
      return ids.map(id => {
        const sessionPart = `_session-${id}_lifetime-${sessionDuration}`;
        const proxyUsername = `${btUsername}${countryPart}${cityPart}${sessionPart}${fastPart}`;
        switch (outputFormat) {
          case 'host:port:username:password':
            return `${protocolPrefix}${host}:${port}:${proxyUsername}:${password}`;
          case 'host:port@username:password':
            return `${protocolPrefix}${host}:${port}@${proxyUsername}:${password}`;
          default:
            return `${protocolPrefix}${proxyUsername}:${password}@${host}:${port}`;
        }
      });
    } else {
      const proxyUsername = `${btUsername}${countryPart}${cityPart}${fastPart}`;
      let single;
      switch (outputFormat) {
        case 'host:port:username:password':
          single = `${protocolPrefix}${host}:${port}:${proxyUsername}:${password}`;
          break;
        case 'host:port@username:password':
          single = `${protocolPrefix}${host}:${port}@${proxyUsername}:${password}`;
          break;
        default:
          single = `${protocolPrefix}${proxyUsername}:${password}@${host}:${port}`;
      }
      return [single];
    }
  };

  const handleCopyProxy = () => {
    const strings = generateProxyStrings();
    navigator.clipboard.writeText(strings.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadProxy = () => {
    const strings = generateProxyStrings();
    const blob = new Blob([strings.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'proxies.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get the residential premium package
  const premiumPackage = packages.find(p => p.proxy_type === 'residential_premium' && p.status === 'active');
  const anyPremiumPackage = packages.find(p => p.proxy_type === 'residential_premium');

  // Purchase modal handlers
  const openPurchaseModal = () => {
    setPurchaseAmount(10);
    setPurchaseInputValue('10');
    setPurchaseError('');
    setShowPurchaseModal(true);
  };

  const handlePurchaseInputChange = (e) => {
    const value = e.target.value;
    setPurchaseInputValue(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1) {
      setPurchaseAmount(Math.min(numValue, 10000));
    }
  };

  const handlePurchaseInputBlur = () => {
    const numValue = parseInt(purchaseInputValue);
    if (isNaN(numValue) || numValue < 1) {
      setPurchaseAmount(1);
      setPurchaseInputValue('1');
    } else {
      const clampedValue = Math.min(numValue, 10000);
      setPurchaseAmount(clampedValue);
      setPurchaseInputValue(String(clampedValue));
    }
  };

  const handlePurchase = async () => {
    setIsPurchasing(true);
    setPurchaseError('');

    const result = await purchasePackage('residential_premium', purchaseAmount);

    if (result.success && result.paymentUrl) {
      setShowPurchaseModal(false);
      window.location.href = result.paymentUrl;
    } else {
      setPurchaseError(result.error || 'Failed to create payment');
      setIsPurchasing(false);
    }
  };

  const formatTierRange = (tier) => {
    if (tier.maxGb) {
      return `${tier.minGb} - ${tier.maxGb} GB`;
    }
    return `${tier.minGb}+ GB`;
  };

  const isActiveTier = (tier, amount) => {
    if (tier.maxGb) {
      return amount >= tier.minGb && amount <= tier.maxGb;
    }
    return amount >= tier.minGb;
  };

  const getExtendedTiers = () => {
    const baseTiers = allTiers['residential_premium'] || [];
    return baseTiers.filter(tier => tier.minGb > 0);
  };

  const proxyStrings = generateProxyStrings();

  return (
    <div className="proxy-config-page">
      <h1 className="page-title">Rotating Residential Premium Proxy</h1>

      <div className="proxy-info-card premium">
        <div className="proxy-info-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
        </div>
        <div className="proxy-info-content">
          <h2>Rotating Residential Premium Proxy</h2>
          <p>Premium tier residential proxies with higher success rates and better IP quality. Ideal for demanding use cases requiring maximum reliability.</p>
        </div>
      </div>

      <div className="tabs-container">
        {hasPremiumAccount && (
          <button
            className={`tab-btn ${activeTab === 'configuration' ? 'active' : ''}`}
            onClick={() => setActiveTab('configuration')}
          >
            CONFIGURATION
          </button>
        )}
        <button
          className={`tab-btn ${activeTab === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          PRICING & BANDWIDTH
        </button>
      </div>

      {activeTab === 'configuration' && (
        <div className="config-layout">
          <div className="config-main">
            {/* Pool & Location */}
            <div className="config-section">
              <h3 className="section-number">01. POOL & LOCATION</h3>
              <div className="section-controls">
                {premiumPackage && (
                  <div className="bandwidth-badge">
                    <span className="badge-dot"></span>
                    <span className="badge-label">BANDWIDTH PACKAGE:</span>
                    <span className="badge-value">{parseFloat(premiumPackage.remaining_gb).toFixed(2)} GB</span>
                  </div>
                )}
                <div className="multi-select-toggle">
                  <span>MULTI-SELECT</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={multiSelect}
                      onChange={(e) => handleMultiSelectToggle(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="search-box">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="location-grid">
                <div className="country-list">
                  {filteredCountries.map(country => (
                    <button
                      key={country.code}
                      className={`country-btn ${selectedCountries.includes(country.code) ? 'active' : ''}`}
                      onClick={() => handleCountryClick(country.code)}
                    >
                      <span className="country-flag">{country.flag}</span>
                      {country.name}
                      {selectedCountries.includes(country.code) && (
                        <svg className="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                      )}
                    </button>
                  ))}
                  <p className="country-hint">{multiSelect ? 'CLICK TO SELECT MULTIPLE' : 'CLICK TO SELECT'}</p>
                </div>
                <div className="city-select-area">
                  {multiSelect ? (
                    <div className="city-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <circle cx="12" cy="10" r="3"/>
                        <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/>
                      </svg>
                      <p>CITY SELECTION NOT AVAILABLE IN MULTI-SELECT MODE</p>
                    </div>
                  ) : selectedCountry === 'global' || availableCities.length === 0 ? (
                    <div className="city-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <circle cx="12" cy="10" r="3"/>
                        <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/>
                      </svg>
                      <p>{selectedCountry === 'global' ? 'GLOBAL TARGETS ALL COUNTRIES' : 'SELECT A COUNTRY TO TARGET CITIES'}</p>
                    </div>
                  ) : (
                    <div className="city-grid">
                      <div className="city-grid-header">
                        <span>Select city in {countries.find(c => c.code === selectedCountry)?.name}</span>
                        {selectedCities.length > 0 && (
                          <button className="clear-cities-btn" onClick={() => setSelectedCities([])}>
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="city-buttons">
                        {availableCities.map(city => (
                          <button
                            key={city}
                            className={`city-btn ${selectedCities.includes(city) ? 'active' : ''}`}
                            onClick={() => handleCityClick(city)}
                          >
                            {city}
                            {selectedCities.includes(city) && (
                              <svg className="check-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20,6 9,17 4,12"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                      {selectedCities.length === 0 && (
                        <p className="city-hint">Leave empty to target all cities in this country</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Format & Protocol */}
            <div className="config-section">
              <h3 className="section-number">02. FORMAT & PROTOCOL</h3>
              <div className="format-grid">
                <div className="form-group">
                  <label>PROXY PROTOCOL</label>
                  <select
                    value={proxyProtocol}
                    onChange={(e) => setProxyProtocol(e.target.value)}
                  >
                    <option value="none">None (Auto Detect)</option>
                    <option value="http">HTTP</option>
                    <option value="socks5">SOCKS5</option>
                  </select>
                  <p className="form-hint">Our proxies support both HTTP and SOCKS protocols on the same port. Choose 'None' if you prefer to specify the protocol in your application.</p>
                </div>
                <div className="form-group">
                  <label>OUTPUT FORMAT</label>
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                  >
                    <option value="username:password@host:port">username:password@host:port</option>
                    <option value="host:port:username:password">host:port:username:password</option>
                    <option value="host:port@username:password">host:port@username:password</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Session Settings */}
            <div className="config-section">
              <h3 className="section-number">
                03. SESSION SETTINGS
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={sessionEnabled}
                    onChange={(e) => setSessionEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </h3>

              {sessionEnabled && (
                <>
                  <div className="session-duration-selector">
                    <label>SESSION DURATION (MINUTES)</label>
                    <div className="duration-buttons">
                      {[1, 5, 10, 15, 30].map(duration => (
                        <button
                          key={duration}
                          className={`duration-btn ${sessionDuration === duration ? 'active' : ''}`}
                          onClick={() => setSessionDuration(duration)}
                        >
                          {duration}m
                        </button>
                      ))}
                      <input
                        type="number"
                        className={`duration-btn duration-input ${![1, 5, 10, 15, 30].includes(sessionDuration) ? 'active' : ''}`}
                        placeholder="Custom"
                        min="1"
                        max="120"
                        defaultValue={![1, 5, 10, 15, 30].includes(sessionDuration) ? sessionDuration : ''}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1 && val <= 120) {
                              setSessionDuration(val);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="session-count-selector">
                    <div className="session-count-header">
                      <label>SESSION COUNT</label>
                      <button className="regenerate-btn" onClick={regenerateSessionIds}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M23 4v6h-6"/>
                          <path d="M1 20v-6h6"/>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                        </svg>
                        REGENERATE
                      </button>
                    </div>
                    <div className="duration-buttons">
                      {[1, 5, 10, 25, 50].map(count => (
                        <button
                          key={count}
                          className={`duration-btn ${sessionCount === count ? 'active' : ''}`}
                          onClick={() => handleSessionCountChange(count)}
                        >
                          {count}
                        </button>
                      ))}
                      <input
                        type="number"
                        className={`duration-btn duration-input ${![1, 5, 10, 25, 50].includes(sessionCount) ? 'active' : ''}`}
                        placeholder="Custom"
                        min="1"
                        max="1000"
                        defaultValue={![1, 5, 10, 25, 50].includes(sessionCount) ? sessionCount : ''}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1 && val <= 1000) {
                              handleSessionCountChange(val);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="session-info-box">
                <div className="info-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                </div>
                <div>
                  <h4>SESSION INFORMATION</h4>
                  <p>Sticky proxy sessions will attempt to keep the same IP address for the duration of the session for the duration of your choice. Sticky session timer starts at the first connection made with the proxy string.</p>
                </div>
              </div>

              <div className="fast-mode-box">
                <div className="fast-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                </div>
                <div className="fast-mode-content">
                  <h4>FAST MODE (EXPERIMENTAL)</h4>
                  <p>RACES MULTIPLE CONNECTIONS FOR UP TO 2X SPEED!</p>
                </div>
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={fastMode}
                    onChange={(e) => setFastMode(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          {/* Proxy Connection Sidebar */}
          <div className="config-sidebar">
            <div className="proxy-connection-card">
              <div className="connection-header">
                <span className="connection-dot premium"></span>
                <h4>PROXY CONNECTION</h4>
                <span className="connection-badge">
                  {sessionEnabled && sessionCount > 1 ? `${sessionCount} SESSIONS` : selectedCities.length > 0 ? selectedCities.length + ' CITIES' : 'SINGLE'}
                </span>
              </div>

              <div className="connection-summary">
                <div className="summary-item">
                  <span className="summary-label">Country:</span>
                  <span className="summary-value">
                    {multiSelect
                      ? (selectedCountries.includes('global')
                        ? 'Global'
                        : selectedCountries.map(c => countries.find(co => co.code === c)?.name).join(', '))
                      : (selectedCountry === 'global' ? 'Global' : countries.find(c => c.code === selectedCountry)?.name)}
                  </span>
                </div>
                {!multiSelect && selectedCities.length > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">City:</span>
                    <span className="summary-value">{selectedCities[0]}</span>
                  </div>
                )}
                {sessionEnabled && (
                  <div className="summary-item">
                    <span className="summary-label">Session:</span>
                    <span className="summary-value">{sessionDuration} min / {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'}</span>
                  </div>
                )}
                {proxyProtocol !== 'none' && (
                  <div className="summary-item">
                    <span className="summary-label">Protocol:</span>
                    <span className="summary-value">{proxyProtocol.toUpperCase()}</span>
                  </div>
                )}
                {fastMode && (
                  <div className="summary-item">
                    <span className="summary-label">Fast Mode:</span>
                    <span className="summary-value summary-active">ENABLED</span>
                  </div>
                )}
              </div>

              <div className="proxy-string-box">
                <code>{proxyStrings.join('\n')}</code>
                <div className="proxy-actions">
                  <button className="copy-proxy-btn" onClick={handleCopyProxy}>
                    {copied ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    )}
                    {copied ? 'Copied!' : proxyStrings.length > 1 ? 'Copy All' : 'Copy'}
                  </button>
                  {proxyStrings.length > 1 && (
                    <button className="download-proxy-btn" onClick={handleDownloadProxy}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download .txt
                    </button>
                  )}
                </div>
              </div>
              <div className="usage-tips">
                <h5>USAGE TIPS</h5>
                <ul>
                  <li>
                    <span className="tip-dot yellow"></span>
                    COPY THE CONNECTION STRINGS AND PASTE THEM INTO YOUR APPLICATION.
                  </li>
                  <li>
                    <span className="tip-dot blue"></span>
                    FOR SESSIONS, EACH STRING ATTEMPTS TO MAINTAIN THE SAME IP FOR THE SPECIFIED DURATION.
                  </li>
                  <li>
                    <span className="tip-dot yellow"></span>
                    YOU'RE ONLY CHARGED FOR THE BANDWIDTH YOU ACTUALLY USE.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pricing' && (
        <div className="pricing-tab-content">
          <div className="pricing-layout">
            <div className="pricing-main">
              <div className="pricing-header">
                <h3>BANDWIDTH PACKAGE PRICING</h3>
                <button className="add-bandwidth-btn" onClick={openPurchaseModal}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  ADD BANDWIDTH
                </button>
              </div>

              <div className="package-section">
                <div className="package-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>PACKAGE PRICING - RESIDENTIAL HIGH TIER</span>
                </div>

                <div className="pricing-tiers">
                  {pricingLoading ? (
                    <div className="loading-text">Loading...</div>
                  ) : (allTiers.residential_premium || []).map((tier, i) => (
                    <div key={i} className="tier-row">
                      <div className="tier-range">
                        <span className="tier-label">PACKAGE RANGE</span>
                        <span className="tier-value">{tier.range}</span>
                      </div>
                      <div className="tier-price">
                        <span className="price-label">PRICE</span>
                        <span className="price-value">${tier.pricePerGb.toFixed(4)}<span>/GB</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pricing-sidebar">
              <div className="bandwidth-package-card">
                <h4>YOUR BANDWIDTH PACKAGE</h4>
                {packagesLoading ? (
                  <div className="no-package">
                    <p>LOADING...</p>
                  </div>
                ) : anyPremiumPackage ? (
                  <div className="active-package">
                    <div className="package-info-header">
                      <span className="package-name">RESIDENTIAL PREMIUM</span>
                      <span className={`package-status ${anyPremiumPackage.status}`}>{anyPremiumPackage.status.toUpperCase()}</span>
                    </div>
                    <div className="package-remaining">
                      <span className="remaining-value">{parseFloat(anyPremiumPackage.remaining_gb).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="remaining-label">GB REMAINING</span>
                    </div>
                    <div className="package-progress">
                      <div className="progress-bar" style={{width: `${(parseFloat(anyPremiumPackage.remaining_gb) / parseFloat(anyPremiumPackage.total_gb)) * 100}%`}}></div>
                    </div>
                  </div>
                ) : (
                  <div className="no-package">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="9" y1="15" x2="15" y2="15"/>
                    </svg>
                    <p>NO ACTIVE BANDWIDTH PACKAGE</p>
                  </div>
                )}
                <button className="purchase-btn" onClick={() => navigate('/dashboard/bandwidth')}>
                  {anyPremiumPackage ? 'MANAGE PACKAGES' : 'PURCHASE BANDWIDTH'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="purchase-modal-overlay" onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            setShowPurchaseModal(false);
          }
        }}>
          <div className="purchase-modal">
            <div className="purchase-modal-header">
              <div className="purchase-header-left">
                <div className="purchase-header-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
                <div className="purchase-header-text">
                  <h3>ADD BANDWIDTH</h3>
                  <p>RESIDENTIAL PREMIUM</p>
                </div>
              </div>
              <button className="purchase-modal-close" onClick={() => setShowPurchaseModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="purchase-amount-section">
              <div className="purchase-amount-header">
                <span className="purchase-amount-label">AMOUNT TO ADD</span>
              </div>
              <div className="purchase-amount-input-wrapper">
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={purchaseInputValue}
                  onChange={handlePurchaseInputChange}
                  onBlur={handlePurchaseInputBlur}
                  className="purchase-amount-input"
                />
                <span className="purchase-amount-suffix">GB</span>
              </div>
            </div>

            <div className="purchase-pricing-section">
              <span className="purchase-pricing-label">PRICING</span>
              <div className="purchase-pricing-list">
                {pricingLoading ? (
                  <div className="purchase-loading">Loading pricing...</div>
                ) : (
                  getExtendedTiers().map((tier, index) => (
                    <div
                      key={index}
                      className={`purchase-tier-item ${isActiveTier(tier, purchaseAmount) ? 'active' : ''}`}
                    >
                      <div className="purchase-tier-indicator">
                        <div className="tier-dot"></div>
                      </div>
                      <span className="purchase-tier-range">{formatTierRange(tier)}</span>
                      <span className="purchase-tier-price">${tier.pricePerGb.toFixed(4)}<span>/GB</span></span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="purchase-total-section">
              <div className="purchase-total-left">
                <span className="purchase-total-label">TOTAL COST</span>
                <span className="purchase-total-amount">
                  <span className="currency">$</span>
                  {calculatePrice('residential_premium', purchaseAmount).totalPrice?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="purchase-total-right">
                <span className="purchase-per-gb">${calculatePrice('residential_premium', purchaseAmount).pricePerGb?.toFixed(4) || '0.0000'}/GB</span>
              </div>
            </div>

            {purchaseError && (
              <div className="purchase-error">
                {purchaseError}
              </div>
            )}

            <div className="purchase-modal-footer">
              <button
                className="purchase-cancel-btn"
                onClick={() => setShowPurchaseModal(false)}
                disabled={isPurchasing}
              >
                CANCEL
              </button>
              <button
                className="purchase-confirm-btn"
                onClick={handlePurchase}
                disabled={isPurchasing || purchaseAmount < 1}
              >
                {isPurchasing ? 'CREATING PAYMENT...' : 'PAY WITH CRYPTO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RotatingResidentialPremiumPage;
